/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/bravada-band-helpers.ts
 * FILE_ID: QB-AGGRID-BRAVADA-HELPERS-001
 * PURPOSE: Small pure I→O helpers that the Bravada band schema files
 *          (bravada-column-bands.ts + bravada-pricing-bands.ts) both
 *          consume — column builders and the shared width table.
 *
 * WHY: Splitting the schema across two files (identity/labour vs
 *      pricing) keeps each file under the 300-line cap. Without a
 *      shared helper module each file would duplicate the same
 *      textCol/numCol builders and width constants. This module is
 *      the single source of truth for both.
 *
 * RELATED:
 *   - QB-AGGRID-BRAVADA-BANDS-001 (bravada-column-bands.ts)
 *   - QB-AGGRID-BRAVADA-PRICING-001 (bravada-pricing-bands.ts)
 */

import type { Column } from '../scope-sheet-grid/ScopeSheetGridTypes'

/**
 * Pure column factory for free-text cells.
 * I→O. No closures over module state.
 */
export function textCol(opts: {
  id: string
  label: string
  width: number
  editable?: boolean
}): Column {
  return {
    id: opts.id,
    label: opts.label,
    width: opts.width,
    editable: opts.editable ?? true,
  }
}

/**
 * Pure column factory for numeric / currency / percent cells.
 * Pass `computed: true` to mark the column as engine-derived (read-only,
 * routed through getComputedValue in scope-ag-grid-formatters.ts).
 */
export function numCol(opts: {
  id: string
  label: string
  width: number
  editable?: boolean
  computed?: boolean
  override?: boolean
}): Column {
  return {
    id: opts.id,
    label: opts.label,
    width: opts.width,
    align: 'right',
    editable: opts.editable ?? !opts.computed,
    computed: opts.computed,
    override: opts.override,
  }
}

/**
 * Column widths calibrated against the default AG-Grid theme at 13px /
 * system-ui font with 16px padding on each side. Each entry is the
 * minimum width that fits the FULL Excel header label without
 * truncation — Bravada estimators specifically asked for no "P..." /
 * "H..." / "D..." abbreviations.
 *
 * Widths grouped by band so future column moves keep neighbouring
 * column widths consistent. New band? Add a new group here.
 */
export const WIDTH = {
  // Identity / Description / Measure
  itemNumber: 64,
  location: 120,
  description: 240,
  productSystem: 170,
  area: 110,
  subTotalExclGst: 130,

  // Total Chargeout
  rateAdjusted: 140,
  rateCalculated: 140,
  totalDollars: 130,

  // Labour
  pmpd: 180,
  manDays: 150,
  manHours: 150,
  labourCost: 140,
  labourChargeout: 170,

  // Products — per-unit + aggregate
  unitCost: 100,
  coverage: 110,
  totalCost: 120,
  wastagePercent: 110,
  perM2: 110,
  withWastage: 130,
  markupPercent: 110,
  markupAmount: 120,
  chargeOut: 130,
  chargeOutPerM2: 130,

  // Plant / Subcontractor
  rentDescription: 200,
  rentUnits: 90,
  rentUnitCost: 110,
  rentTotalCost: 120,
  rentMarkupPercent: 110,
  rentMarkupAmount: 110,
  rentChargeOut: 130,

  // Per-line OVERRIDES band (MR H) — slightly narrower than the
  // computed columns above because override cells show a single
  // percentage value (no "13.2% (calc)" trailing text) and we want
  // them visually distinct + scannable on a wide screen.
  overridePercent: 105,
} as const
